import { Inject, Injectable } from '@angular/core';
import {
  CarrierModelKeys, PRODUCTION_RECONCILIATION_METRIC_DEFINITIONS,
  ProductionReconciliationCarrier, ProductionReconciliationMetricKey,
  ProductionReconciliationRecord,
} from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from '../../../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../../../injections/firebase-app';
import { CarrierService } from '../../../services/carrier.service';
import {
  ProductionReconciliationDashboardColumnKey,
  ProductionReconciliationDashboardData,
  ProductionReconciliationDashboardRow,
  ProductionReconciliationDashboardSetup,
  ProductionReconciliationDualValue,
  ProductionReconciliationPeriodColumn,
  RECONCILIATION_COLLECTION,
  buildProductionReconciliationMetricFieldName,
  getCarrierMetricsFromRecord,
  mapCarriersForReconciliationDashboard,
  resolveReportingPeriodIndices,
} from './production-reconciliation-dashboard.models';
import { formatReportingPeriodColumnCaption, getReportingPeriodRange } from './reporting-week.utils';

const FIRESTORE_IN_QUERY_LIMIT = 30;

@Injectable()
export class ProductionReconciliationDashboardService {
  private readonly fsDao: CommonFireStoreDao<ProductionReconciliationRecord>;

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private readonly carrierService: CarrierService,
  ) {
    this.fsDao = new CommonFireStoreDao<ProductionReconciliationRecord>(fireBaseApp, null, null);
  }

  async loadDashboardData(
    setup: ProductionReconciliationDashboardSetup,
  ): Promise<ProductionReconciliationDashboardData> {
    const periodIndices = resolveReportingPeriodIndices(setup);
    const [carriers, recordsByYearAndWeek] = await Promise.all([
      this.loadCarriers(),
      this.loadRecordsByYearAndWeeks(setup, periodIndices),
    ]);
    const periods = this.buildPeriodColumns(setup, periodIndices);
    const rows = this.buildRows(setup, periodIndices, recordsByYearAndWeek, carriers);

    return {
      periods,
      rows,
    };
  }

  private async loadCarriers(): Promise<ProductionReconciliationCarrier[]> {
    const carriers = await this.carrierService.getAll(CarrierModelKeys.carrierName);
    console.log('loadCarriers records', carriers);

    return mapCarriersForReconciliationDashboard(carriers);
  }

  private buildPeriodColumns(
    setup: ProductionReconciliationDashboardSetup,
    periodIndices: number[],
  ): ProductionReconciliationPeriodColumn[] {
    return periodIndices.map(periodIndex => {
      const currentRange = getReportingPeriodRange(setup.currentYear, periodIndex);
      const compareRange = getReportingPeriodRange(setup.compareYear, periodIndex);

      return {
        periodIndex,
        caption: formatReportingPeriodColumnCaption(currentRange, compareRange),
        metrics: PRODUCTION_RECONCILIATION_METRIC_DEFINITIONS.map(metric => ({
          key: metric.key,
          label: metric.label,
          dataField: buildProductionReconciliationMetricFieldName(periodIndex, metric.key),
          format: metric.format,
        })),
      };
    });
  }

  private async loadRecordsByYearAndWeeks(
    setup: ProductionReconciliationDashboardSetup,
    periodIndices: number[],
  ): Promise<Map<string, ProductionReconciliationRecord>> {
    const [currentYearRecords, compareYearRecords] = await Promise.all([
      this.loadRecordsForYear(setup.currentYear, periodIndices),
      this.loadRecordsForYear(setup.compareYear, periodIndices),
    ]);

    const recordsByYearAndWeek = new Map<string, ProductionReconciliationRecord>();

    currentYearRecords.forEach((record, week) => {
      recordsByYearAndWeek.set(this.buildRecordLookupKey(setup.currentYear, week), record);
    });

    compareYearRecords.forEach((record, week) => {
      recordsByYearAndWeek.set(this.buildRecordLookupKey(setup.compareYear, week), record);
    });

    return recordsByYearAndWeek;
  }

  private async loadRecordsForYear(
    year: number,
    weeks: number[],
  ): Promise<Map<number, ProductionReconciliationRecord>> {
    const recordsByWeek = new Map<number, ProductionReconciliationRecord>();
    const uniqueWeeks = [...new Set(weeks)];

    for (const weekChunk of this.chunkValues(uniqueWeeks, FIRESTORE_IN_QUERY_LIMIT)) {
      const records = await this.fsDao.getAllByQValue(RECONCILIATION_COLLECTION, [
        new QueryParam('year', WhereFilterOperandKeys.equal, year),
        new QueryParam('week', WhereFilterOperandKeys.in, weekChunk),
      ]);

      console.log('loadRecordsForYear records', records);

      records.forEach(record => {
        if (record?.week == null) {
          return;
        }

        recordsByWeek.set(record.week, record);
      });
    }

    return recordsByWeek;
  }

  private buildRows(
    setup: ProductionReconciliationDashboardSetup,
    periodIndices: number[],
    recordsByYearAndWeek: Map<string, ProductionReconciliationRecord>,
    carriers: ProductionReconciliationCarrier[],
  ): ProductionReconciliationDashboardRow[] {
    return carriers.map(carrier => {
      const row: ProductionReconciliationDashboardRow = {
        id: carrier.id,
        [ProductionReconciliationDashboardColumnKey.carrierName]: carrier.name,
      };

      periodIndices.forEach(periodIndex => {
        const currentRecord = recordsByYearAndWeek.get(this.buildRecordLookupKey(setup.currentYear, periodIndex));
        const compareRecord = recordsByYearAndWeek.get(this.buildRecordLookupKey(setup.compareYear, periodIndex));
        const currentMetrics = getCarrierMetricsFromRecord(currentRecord, carrier.id);
        const compareMetrics = getCarrierMetricsFromRecord(compareRecord, carrier.id);

        PRODUCTION_RECONCILIATION_METRIC_DEFINITIONS.forEach(metric => {
          row[buildProductionReconciliationMetricFieldName(periodIndex, metric.key)] = {
            current: this.getMetricValue(currentMetrics, metric.key),
            compare: this.getMetricValue(compareMetrics, metric.key),
          } satisfies ProductionReconciliationDualValue;
        });
      });

      return row;
    });
  }

  private getMetricValue(
    metrics: ReturnType<typeof getCarrierMetricsFromRecord>,
    metricKey: ProductionReconciliationMetricKey,
  ): number | null {
    if (!metrics) {
      return null;
    }

    const value = metrics[metricKey];

    return value == null ? null : value;
  }

  private buildRecordLookupKey(year: number, week: number): string {
    return `${year}:${week}`;
  }

  private chunkValues<T>(values: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];

    for (let index = 0; index < values.length; index += chunkSize) {
      chunks.push(values.slice(index, index + chunkSize));
    }

    return chunks;
  }
}
